import {
  DEFECT_DEPARTMENTS,
  DEFECT_KINDS,
  DEFECT_PRIORITIES,
  DEFECT_SEVERITIES,
  EQUIPMENT_STATUSES,
} from '@felix/domain';
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Group,
  Loader,
  Select,
  Tabs,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconCheck,
  IconDeviceFloppy,
  IconEdit,
  IconFileTypePdf,
  IconPaperclip,
  IconPlus,
  IconPrinter,
  IconSend,
  IconX,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { TimeInput } from '../../inputs/TimeInput.js';
import { MasterSectionNote, masterLocked } from '../../master-section.js';
import { Panel } from '../../Panel.js';
import { useUnsavedChanges } from '../../shell-context.js';
import { DrillSourceLink } from '../drills/DrillFindings.js';
import table from '../materials/Materials.module.css';
import { browserPdfOutput, pdfErrorKey, renderPdf } from '../personnel/pdf.js';
import { AttachmentCard } from '../pms/AttachmentCard.js';
import type { PmsAttachment } from '../pms/types.js';
import { ReturnedBanner } from '../returned/Returned.js';
import {
  MaterialTables,
  NonConformityTable,
  PartCard,
  QUICK_ICONS,
  type QuickAction,
  SidePanel,
  SignRow,
  WorkflowTable,
  WorkOrdersTable,
} from './DefectLgsParts.js';
import {
  ActionDialog,
  DefectActionBadge,
  DefectClosureRoute,
  DefectError,
  DefectLoadError,
  DefectStageBadge,
  DefectStepper,
  defectErrorText,
  NoteDialog,
  SignDialog,
  StockDialog,
} from './DefectParts.js';
import classes from './Defects.module.css';
import {
  createInputOf,
  type DefectFormState,
  formOf,
  newForm,
  nextStatuses,
  patchOf,
} from './defect-form.js';
import { defectPdf, defectPdfFileName } from './defect-pdf.js';
import type { DefectsSource } from './source.js';
import type {
  DefectActionInput,
  DefectApprovalStep,
  DefectDetail,
  DefectHseLink,
  DefectOptions,
  DefectSignature,
  DefectStockOption,
} from './types.js';

type Dialog =
  | { kind: 'action'; index: number | null }
  | { kind: 'followup' }
  | { kind: 'management' }
  | { kind: 'comment' }
  | { kind: 'submit' }
  | { kind: 'decide'; decision: 'approved' | 'returned' }
  | { kind: 'reject' }
  | { kind: 'stock' }
  | null;

const TABS = [
  'form',
  'workflow',
  'workOrders',
  'nonConformities',
  'material',
  'photos',
  'documents',
] as const;
type Tab = (typeof TABS)[number];

export interface DefectReportProps {
  source: DefectsSource;
  /** Yeni arızada `null`. */
  id: string | null;
  /** Yeni arıza: ekipman kartından. */
  equipmentId?: string | null;
  /** Yeni arıza: günlük Defect/Observation'dan dönüştürme (ADR 0060 §7). */
  dailyReportId?: string | null;
  /** Yeni arıza: HSEQ raporundan (ADR 0096 §5). */
  hseReportId?: string | null;
  /** Yeni arıza: PMS bakım iş emrinden (ADR 0096 §6). */
  workOrderId?: string | null;
  onBack: () => void;
  onOpen: (id: string) => void;
  /** HSE Deficiency yönlendirmesi (ADR 0060 §3). */
  onHse?: () => void;
  /** Kaynak günlük raporu açar (rapor günüyle). */
  onDaily?: (id: string, day: string | null) => void;
  /** Bağlı kayıtlar (ADR 0096 §3, §4). */
  /** İş emrini açar; ofiste geminin seçimiyle (`vesselId`). */
  onWorkOrder?: (id: string, vesselId: string | null) => void;
  onNonConformity?: (link: Pick<DefectHseLink, 'id' | 'reportType'>) => void;
  onRequisition?: (id: string) => void;
  onNewRequisition?: (defect: { id: string; defectNo: string }) => void;
  onNewNonConformity?: (defect: { id: string; defectNo: string }) => void;
}

/**
 * Arıza Bildirim Formu, LGS-17 (`ariza-ekran.jpeg`; ADR 0096): başlıkta numara, aşama rozeti,
 * oluşturma, kaynak ve oluşturan; Yazdır ve PDF; dört ana aşama; sekmeler Form (Part A/B/C ve SMS
 * takibi, ayrıntılar katlanır), Workflow / Geçmiş, İş Emirleri, Uygunsuzluklar, Malzeme,
 * Fotoğraflar ve İlgili Dokümanlar; sağda İlgili Kayıtlar, Durum, Basamaklar ve Hızlı İşlemler.
 * Gemide yazılır; ofiste salt okunur, Part C notu ve yönetim kararı. Her rol yalnız kendi
 * işlemlerini görür (sunucunun `can`'ı).
 */
export function DefectReport({
  source,
  id,
  equipmentId = null,
  dailyReportId = null,
  hseReportId = null,
  workOrderId = null,
  onBack,
  onOpen,
  onHse,
  onDaily,
  onWorkOrder,
  onNonConformity,
  onRequisition,
  onNewRequisition,
  onNewNonConformity,
}: DefectReportProps) {
  const { t } = useTranslation();
  const d = (key: string, options: Record<string, unknown> = {}) => t(`defects.${key}`, options);
  const f = (key: string) => d(`record.fields.${key}`);
  const l = (key: string, options: Record<string, unknown> = {}) => d(`lgs.${key}`, options);
  const ship = source.scope === 'ship';
  const editor = ship ? source.editor : undefined;
  const tzOffsetMin = source.tzOffsetMin ?? 0;

  const [detail, setDetail] = useState<DefectDetail | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [options, setOptions] = useState<DefectOptions | null>(null);
  const [form, setForm] = useState<DefectFormState | null>(null);
  const [attachments, setAttachments] = useState<PmsAttachment[]>([]);
  const [stock, setStock] = useState<DefectStockOption[] | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('form');
  const [printing, setPrinting] = useState<'print' | 'pdf' | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [next, opts] = await Promise.all([
        id ? source.detail(id) : Promise.resolve(null),
        editor
          ? editor.options(id ? {} : { dailyReportId, hseReportId, workOrderId })
          : Promise.resolve(null),
      ]);
      setOptions(opts);
      setDetail(next);
      setForm(
        next
          ? formOf(next)
          : newForm({
              now: new Date().toISOString(),
              tzOffsetMin,
              equipmentId,
              observation: opts?.observation ?? null,
              hseReport: opts?.hseReport ?? null,
              sourceWorkOrder: opts?.sourceWorkOrder ?? null,
            }),
      );
      if (next && source.attachments) {
        setAttachments(await source.attachments(next.id).catch(() => []));
      }
    } catch (e) {
      setLoadError(e);
    }
  }, [id, source, editor, dailyReportId, hseReportId, workOrderId, equipmentId, tzOffsetMin]);
  useEffect(() => {
    void load();
  }, [load]);

  const locked = detail !== null && (detail.closure.status === 'pending' || detail.closure.closed);
  // Gemide düzeltme yetkisi sunucudan (`can.edit`); eski sunucuda varsayılan açık
  const editable = Boolean(editor) && !locked && detail?.can?.edit !== false;
  const patch = useMemo(() => (form && detail ? patchOf(form, detail) : {}), [form, detail]);
  const dirty = detail ? Object.keys(patch).length > 0 : form !== null;
  useUnsavedChanges(Boolean(detail) && dirty);

  const equipmentOptions = useMemo(
    () => (options?.equipment ?? []).map((e) => ({ value: e.id, label: `${e.code} · ${e.name}` })),
    [options],
  );
  const selected = options?.equipment.find((e) => e.id === form?.equipmentId) ?? null;
  const jobOptions = useMemo(
    () =>
      (options?.jobs ?? [])
        .filter((j) => !form?.equipmentId || j.equipmentId === form.equipmentId)
        .map((j) => ({ value: j.id, label: `${j.code} · ${j.title}` })),
    [options, form?.equipmentId],
  );

  if (loadError) {
    return (
      <div className={classes.screen}>
        <DefectLoadError error={loadError} onRetry={() => void load()} />
      </div>
    );
  }
  if (!form || (id && !detail)) {
    return (
      <div className={classes.screen}>
        <Group gap="sm">
          <Loader size="sm" />
          <span className={classes.muted}>{d('record.loading')}</span>
        </Group>
      </div>
    );
  }

  const change = (next: Partial<DefectFormState>) => setForm((v) => (v ? { ...v, ...next } : v));
  const run = async (task: () => Promise<DefectDetail | null>, message?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await task();
      if (next) {
        setDetail(next);
        setForm(formOf(next));
      }
      if (message) setNotice(message);
    } catch (e) {
      setError(defectErrorText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    if (!editor) return;
    if (!detail) {
      setBusy(true);
      setError(null);
      try {
        const created = await editor.create(
          createInputOf(form, {
            tzOffsetMin,
            dailyReportId: options?.observation?.id ?? null,
            hseReportId: options?.hseReport?.id ?? null,
            sourceWorkOrderId: options?.sourceWorkOrder?.id ?? null,
          }),
        );
        onOpen(created.id);
      } catch (e) {
        setError(defectErrorText(t, e));
      } finally {
        setBusy(false);
      }
      return;
    }
    await run(() => editor.update(detail.id, detail.rowVersion, patch), d('record.saved'));
  };
  const saveActions = async (actions: DefectActionInput[]) => {
    if (!editor || !detail) return;
    const next = await editor.update(detail.id, detail.rowVersion, { actions });
    setDetail(next);
  };
  const output = async (mode: 'print' | 'pdf') => {
    if (!detail) return;
    setPrinting(mode);
    setError(null);
    try {
      const brand = (await source.brand?.().catch(() => null)) ?? null;
      const tr = (key: string, opts?: Record<string, unknown>) => String(t(key, opts ?? {}));
      const definition = defectPdf(tr, {
        defect: detail,
        vessel: {
          name: detail.vesselName ?? source.vesselName ?? '',
          imo: source.vesselImo ?? null,
        },
        brand,
        documents: attachments.filter((a) => !a.voided).map((a) => a.fileName),
        printedAt: new Date().toISOString(),
      });
      const target = source.pdf ?? browserPdfOutput();
      const fileName = defectPdfFileName(detail.defectNo, d('record.title'));
      if (mode === 'print') {
        await target.print(fileName, () => renderPdf(definition, { print: true }));
      } else {
        await target.save(fileName, () => renderPdf(definition));
      }
    } catch (e) {
      const code = (e as { code?: unknown } | null)?.code;
      const key = pdfErrorKey(typeof code === 'string' ? code : '');
      setError(key === 'restHours.pdf.error' ? d('pdf.error') : t(key));
    } finally {
      setPrinting(null);
    }
  };

  const pendingStep =
    detail && detail.closure.status === 'pending'
      ? (detail.signatures.steps.find((s) => s.step === detail.closure.step) ?? null)
      : null;
  const canShipDecide = Boolean(editor && pendingStep && !pendingStep.office);
  const canOfficeDecide = !ship && Boolean(detail?.can?.approve || detail?.can?.return);
  const canSubmit =
    Boolean(editor) &&
    detail !== null &&
    !dirty &&
    detail.status === 'verification' &&
    (detail.closure.status === 'draft' || detail.closure.status === 'returned');
  const decide = async (input: {
    decision: 'approved' | 'returned';
    password?: string;
    reason: string | null;
  }) => {
    if (!detail || detail.closure.submissionId === null || detail.closure.step === null) return;
    const body = {
      ...input,
      contentHash: detail.contentHash,
      submissionId: detail.closure.submissionId,
      step: detail.closure.step,
    };
    const next = ship
      ? await editor?.decide(detail.id, body)
      : await source.office?.decide(detail.id, body);
    if (next) {
      setDetail(next);
      setForm(formOf(next));
    }
  };

  const readOnly = !editable;
  // Part B kaptanın (LGS-17; ADR 0127 §1, §2): gemide kaptan ya da vekil yazar; eski sunucuda kilit yok
  const masterCapacity = ship ? (detail ? detail.can?.master : options?.master) : undefined;
  const partBLocked = masterLocked(masterCapacity);
  const reauthKind = detail?.kind === 'breakdown' ? 'breakdown_report' : 'defect_report';
  const managementNotes = detail?.officeNotes.filter((n) => n.kind === 'management_action') ?? [];
  const links = detail?.links;
  const workOrders = links?.workOrders ?? [];
  const liveOrders = workOrders.filter((w) => !w.cancelled);
  const readyForVerification =
    detail !== null &&
    detail.stage === 'under_action' &&
    liveOrders.length > 0 &&
    liveOrders.every((w) => w.reported);
  const can = detail?.can;
  const openWorkOrder = onWorkOrder
    ? (workOrderId: string) => onWorkOrder(workOrderId, detail?.vesselId ?? null)
    : undefined;
  const history = detail
    ? [
        ...detail.followups.map((n) => ({
          id: n.id,
          at: n.at,
          offset: n.tzOffsetMin,
          body: n.body,
          by: n.byName ?? '—',
        })),
        ...detail.officeNotes
          .filter((n) => n.kind === 'comment')
          .map((n) => ({
            id: n.id,
            at: n.at,
            offset: 0,
            body: n.body,
            by: `${n.byName ?? '—'} (${d('followups.office')})`,
          })),
      ].sort((a, b) => a.at.localeCompare(b.at))
    : [];

  /* ---------- Başlık ---------- */

  const sourceLabel = (() => {
    const ref = detail?.sourceRef;
    const hse = options?.hseReport;
    const wo = options?.sourceWorkOrder;
    if (ref?.kind === 'hse_report' || (!detail && hse)) {
      const type = ref?.reportType ?? hse?.reportType ?? '';
      return l('sourceHse', {
        type: t(`hse.types.${type}`, { defaultValue: type }),
        no: ref?.no ?? hse?.reportNo ?? '',
      });
    }
    if (ref?.kind === 'work_order' || (!detail && wo)) {
      return l('sourceWorkOrder', { no: ref?.no ?? wo?.woNo ?? '' });
    }
    if (ref?.kind === 'daily_report' || (!detail && options?.observation)) {
      const day = ref?.no ?? options?.observation?.reportDay ?? null;
      return l('sourceDaily', { no: day ? formatDay(day) : '' });
    }
    if (detail && detail.source !== 'manual') return d(`record.sources.${detail.source}`);
    return l('sourceDirect');
  })();

  const header = (
    <header className={classes.lgsHead}>
      <div className={classes.lgsTitle}>
        <span className={classes.lgsEyebrow}>{l('eyebrow')}</span>
        <div className={classes.lgsTitleRow}>
          <h1>{detail ? detail.defectNo : d('record.newTitle')}</h1>
          {detail ? (
            <DefectStageBadge stage={detail.stage} awaitingParts={detail.awaitingParts} />
          ) : null}
        </div>
        <p className={classes.lgsMeta}>
          {detail ? (
            <span>
              {l('created', { at: formatDateTime(detail.reportedAt, detail.tzOffsetMin) })}
            </span>
          ) : null}
          <span>{l('source', { source: sourceLabel })}</span>
          {detail?.links?.drillSource ? (
            <DrillSourceLink
              source={detail.links.drillSource}
              vesselId={source.scope === 'office' ? detail.vesselId : null}
            />
          ) : null}
          {detail ? (
            <span>
              {l('createdBy', {
                name: `${detail.reportedBy.name ?? '—'}${detail.reportedBy.roleName ? ` (${detail.reportedBy.roleName})` : ''}`,
              })}
            </span>
          ) : null}
        </p>
      </div>
      <div className={classes.recordActions}>
        {detail ? (
          <>
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              loading={printing === 'print'}
              onClick={() => void output('print')}
            >
              {l('print')}
            </Button>
            <Button
              variant="default"
              leftSection={<IconFileTypePdf size={16} />}
              loading={printing === 'pdf'}
              onClick={() => void output('pdf')}
            >
              {l('pdf')}
            </Button>
          </>
        ) : null}
        {editable ? (
          <Button
            variant={detail ? 'default' : 'filled'}
            leftSection={<IconDeviceFloppy size={16} />}
            loading={busy}
            disabled={Boolean(detail) && !dirty}
            onClick={() => void save()}
          >
            {detail ? d('record.save') : d('record.create')}
          </Button>
        ) : null}
        {canSubmit ? (
          <Button
            leftSection={<IconSend size={16} />}
            onClick={() => setDialog({ kind: 'submit' })}
          >
            {d('record.submit')}
          </Button>
        ) : null}
        {canShipDecide || canOfficeDecide ? (
          <>
            <Button
              variant="default"
              color="red"
              leftSection={<IconX size={16} />}
              onClick={() => setDialog({ kind: 'decide', decision: 'returned' })}
            >
              {d('record.return')}
            </Button>
            <Button
              leftSection={<IconCheck size={16} />}
              onClick={() => setDialog({ kind: 'decide', decision: 'approved' })}
            >
              {d('record.approve')}
            </Button>
          </>
        ) : null}
      </div>
    </header>
  );

  /* ---------- Form (LGS-17) ---------- */

  const equipmentField =
    editable && (!detail || detail.equipmentId === null) ? (
      <Select
        label={l('fields.equipment')}
        placeholder={f('equipmentNone')}
        data={equipmentOptions}
        value={form.equipmentId}
        searchable
        clearable
        nothingFoundMessage="—"
        onChange={(value) => change({ equipmentId: value, jobDefinitionId: null })}
      />
    ) : (
      <TextInput
        label={l('fields.equipment')}
        value={
          detail?.equipment
            ? [detail.equipment.code, detail.equipment.name].filter(Boolean).join(' · ')
            : selected
              ? `${selected.code} · ${selected.name}`
              : f('equipmentNone')
        }
        readOnly
      />
    );

  const photos = (
    <div className={classes.cardBody}>
      {attachments.length === 0 ? (
        <p className={classes.muted}>{d('record.documentsEmpty')}</p>
      ) : (
        <ul className={classes.files}>
          {attachments.map((a) => (
            <AttachmentCard
              key={a.id}
              attachment={a}
              {...(source.attachmentUrl ? { href: source.attachmentUrl(a.id) } : {})}
            />
          ))}
        </ul>
      )}
      {detail && editor?.upload && !detail.closure.closed ? (
        <div className={classes.upload}>
          <Button
            component="label"
            size="xs"
            variant="light"
            leftSection={<IconPaperclip size={14} />}
            loading={busy}
          >
            {d('record.addPhoto')}
            <input
              type="file"
              hidden
              accept="image/jpeg,image/png,application/pdf"
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = '';
                if (!file || !editor.upload) return;
                void run(async () => {
                  await editor.upload?.(detail.id, file);
                  setAttachments(await (source.attachments?.(detail.id) ?? Promise.resolve([])));
                  return null;
                });
              }}
            />
          </Button>
        </div>
      ) : null}
    </div>
  );

  const partA = (
    <PartCard
      id="defect-part-a"
      part="a"
      no={1}
      title={l('parts.a')}
      hint={l('parts.aHint')}
      done={Boolean(detail)}
      at={detail ? formatDateTime(detail.reportedAt, detail.tzOffsetMin) : null}
    >
      <div className={classes.grid3}>
        <TextInput
          label={l('fields.crewName')}
          value={detail?.reportedBy.name ?? l('fields.you')}
          readOnly
        />
        <TextInput
          label={l('fields.position')}
          value={detail?.reportedBy.roleName ?? '—'}
          readOnly
        />
        {equipmentField}
        {form.equipmentId === null ? (
          <TextInput
            label={f('systemName')}
            required={editable}
            value={form.systemName}
            maxLength={200}
            onChange={(event) => change({ systemName: event.currentTarget.value })}
            readOnly={readOnly}
          />
        ) : null}
        <TextInput
          label={l('fields.location')}
          value={form.location}
          maxLength={200}
          onChange={(event) => change({ location: event.currentTarget.value })}
          readOnly={readOnly}
        />
        {!detail ? (
          <Group grow gap={8} align="end">
            <DayInput
              label={f('dateReported')}
              required
              value={form.reportedDay}
              onChange={(reportedDay) => change({ reportedDay })}
            />
            <TimeInput
              aria-label={f('dateReported')}
              value={form.reportedTime}
              onChange={(reportedTime) => change({ reportedTime })}
            />
          </Group>
        ) : null}
      </div>
      <Textarea
        label={l('fields.description')}
        required={editable}
        autosize
        minRows={3}
        maxLength={4000}
        value={form.description}
        onChange={(event) => change({ description: event.currentTarget.value })}
        readOnly={readOnly}
      />
      <Textarea
        label={l('fields.actionRequired')}
        autosize
        minRows={2}
        maxLength={2000}
        value={form.actionRequired}
        onChange={(event) => change({ actionRequired: event.currentTarget.value })}
        readOnly={readOnly}
      />
      {detail ? photos : null}
    </PartCard>
  );

  const partB = (
    <PartCard
      id="defect-part-b"
      part="b"
      no={2}
      title={l('parts.b')}
      hint={l('parts.bHint')}
      done={Boolean(detail?.immediateAction)}
      at={detail?.captainActionOn ? formatDay(detail.captainActionOn) : null}
    >
      <div className={classes.withDate}>
        <Textarea
          label={l('fields.actionTaken')}
          autosize
          minRows={2}
          maxLength={4000}
          value={form.immediateAction}
          onChange={(event) => change({ immediateAction: event.currentTarget.value })}
          readOnly={readOnly || partBLocked}
        />
        <DayInput
          label={l('fields.dateRectified')}
          value={form.rectifiedOn}
          onChange={(rectifiedOn) => change({ rectifiedOn })}
          readOnly={readOnly || !detail}
          clearable={!readOnly && Boolean(detail)}
        />
      </div>
      <MasterSectionNote
        capacity={masterCapacity}
        author={detail?.partBAuthor}
        editable={!readOnly}
      />
    </PartCard>
  );

  const partC = detail ? (
    <PartCard
      id="defect-part-c"
      part="c"
      no={3}
      title={l('parts.c')}
      hint={l('parts.cHint')}
      done={managementNotes.length > 0}
      at={managementNotes.at(-1) ? formatDateTime(String(managementNotes.at(-1)?.at)) : null}
    >
      {managementNotes.length === 0 ? (
        <p className={classes.muted}>
          {d('record.managementEmpty')} {ship ? l('managementShip') : ''}
        </p>
      ) : (
        <ul className={classes.notes}>
          {managementNotes.map((note) => (
            <li key={note.id} className={classes.note}>
              <span className={classes.noteMeta}>
                {note.actionOn
                  ? `${l('fields.dateRectified')}: ${formatDay(note.actionOn)} · `
                  : ''}
                {note.byName ?? d('followups.office')} · {formatDateTime(note.at)}
              </span>
              {note.body}
            </li>
          ))}
        </ul>
      )}
      {!ship && can?.managementAction ? (
        <div>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={() => setDialog({ kind: 'management' })}
          >
            {d('record.addManagementAction')}
          </Button>
        </div>
      ) : null}
    </PartCard>
  ) : null;

  const sms = detail ? (
    <PartCard
      id="defect-sms"
      part="sms"
      no={4}
      title={l('parts.sms')}
      hint={l('parts.smsHint')}
      done={detail.procedureReview !== null}
    >
      <Checkbox
        label={l('fields.noUpdate')}
        checked={form.reviewRequired === false}
        disabled={readOnly}
        onChange={(event) =>
          change(
            event.currentTarget.checked
              ? { reviewRequired: false, procedure: '', improvement: '' }
              : { reviewRequired: null },
          )
        }
      />
      <div className={classes.grid2}>
        <TextInput
          label={l('fields.procedures')}
          maxLength={200}
          value={form.procedure}
          disabled={form.reviewRequired === false}
          onChange={(event) => {
            const procedure = event.currentTarget.value;
            change({
              procedure,
              reviewRequired: procedure.trim() || form.improvement.trim() ? true : null,
            });
          }}
          readOnly={readOnly}
        />
        <Textarea
          label={l('fields.improvement')}
          autosize
          minRows={1}
          maxLength={2000}
          value={form.improvement}
          disabled={form.reviewRequired === false}
          onChange={(event) => {
            const improvement = event.currentTarget.value;
            change({
              improvement,
              reviewRequired: improvement.trim() || form.procedure.trim() ? true : null,
            });
          }}
          readOnly={readOnly}
        />
      </div>
      <SignRow detail={detail} />
    </PartCard>
  ) : null;

  const more = (
    <details className={classes.more}>
      <summary>{l('more')}</summary>
      <div className={classes.moreBody}>
        <span className={classes.muted}>{l('moreHint')}</span>
        <div className={classes.grid4}>
          {detail ? (
            <TextInput label={f('kind')} value={d(`kinds.${detail.kind}`)} readOnly />
          ) : (
            <Select
              label={f('kind')}
              data={DEFECT_KINDS.map((value) => ({ value, label: d(`kinds.${value}`) }))}
              value={form.kind}
              allowDeselect={false}
              onChange={(kind) => change({ kind: (kind ?? 'defect') as DefectFormState['kind'] })}
            />
          )}
          <Select
            label={f('severity')}
            data={DEFECT_SEVERITIES.map((value) => ({ value, label: d(`severities.${value}`) }))}
            value={form.severity}
            allowDeselect={false}
            readOnly={readOnly}
            onChange={(severity) =>
              change({ severity: (severity ?? 'major') as DefectFormState['severity'] })
            }
          />
          <Select
            label={f('priority')}
            data={DEFECT_PRIORITIES.map((value) => ({ value, label: d(`priorities.${value}`) }))}
            value={form.priority}
            onChange={(priority) => change({ priority })}
            readOnly={readOnly}
            clearable={!readOnly}
          />
          <Select
            label={f('department')}
            data={DEFECT_DEPARTMENTS.map((value) => ({
              value,
              label: d(`departments.${value}`),
            }))}
            value={form.department}
            onChange={(department) => change({ department })}
            readOnly={readOnly}
            clearable={!readOnly}
          />
          <DayInput
            label={f('targetOn')}
            value={form.targetOn}
            onChange={(targetOn) => change({ targetOn })}
            readOnly={readOnly}
            clearable={!readOnly}
          />
          <DayInput
            label={f('captainActionOn')}
            value={form.captainActionOn}
            onChange={(captainActionOn) => change({ captainActionOn })}
            readOnly={readOnly || partBLocked}
            clearable={!readOnly && !partBLocked}
          />
          {editable ? (
            <Select
              label={f('job')}
              placeholder={f('jobNone')}
              data={jobOptions}
              value={form.jobDefinitionId}
              searchable
              clearable
              onChange={(jobDefinitionId) => change({ jobDefinitionId })}
            />
          ) : (
            <TextInput
              label={f('job')}
              value={
                detail?.job
                  ? [detail.job.code, detail.job.title].filter(Boolean).join(' · ')
                  : f('jobNone')
              }
              readOnly
            />
          )}
          <TextInput
            label={f('category')}
            value={selected?.category ?? detail?.equipment?.category ?? '—'}
            readOnly
          />
        </div>
        <Checkbox
          label={f('operationalImpact')}
          checked={form.operationalImpact}
          disabled={readOnly}
          onChange={(event) => change({ operationalImpact: event.currentTarget.checked })}
        />
        {detail ? (
          <div className={classes.grid2}>
            {(
              [
                'investigation',
                'rootCause',
                'correctiveAction',
                'testResult',
                'noRepairReason',
                'classNotificationRef',
                'classNotificationWaiver',
              ] as const
            ).map((key) => (
              <Textarea
                key={key}
                label={f(key)}
                autosize
                minRows={1}
                maxLength={4000}
                value={form[key]}
                onChange={(event) => change({ [key]: event.currentTarget.value })}
                readOnly={readOnly}
              />
            ))}
            <Select
              label={f('conditionAfter')}
              data={EQUIPMENT_STATUSES.map((value) => ({
                value,
                label: d(`conditions.${value}`, { defaultValue: value }),
              }))}
              value={form.conditionAfter}
              onChange={(conditionAfter) => change({ conditionAfter })}
              readOnly={readOnly}
              clearable={!readOnly}
            />
          </div>
        ) : null}
      </div>
    </details>
  );

  const hse = !detail ? (
    <div className={classes.banner} data-tone="warning" role="note">
      <span className={classes.bannerTitle}>
        <IconAlertTriangle size={14} aria-hidden /> {d('record.hseTitle')}
      </span>
      {d('record.hseBody')}
      {onHse ? (
        <div className={classes.bannerActions}>
          {/* Uyarı bandında beyaz düğme, uyarı tonunda yazı (temanın `warning` paleti; WCAG 1.4.3) */}
          <Button size="xs" variant="white" color="warning" onClick={onHse}>
            {d('record.hseOpen')}
          </Button>
        </div>
      ) : null}
    </div>
  ) : null;

  /* ---------- Workflow / Geçmiş ---------- */

  const actions = detail?.actions ?? [];
  const stageControl =
    detail && editable ? (
      <Select
        label={l('workflow.stageControl')}
        data={nextStatuses(detail.status).map((value) => ({
          value,
          label: d(`statuses.${value}`),
        }))}
        value={form.status}
        allowDeselect={false}
        onChange={(value) => change({ status: value ?? detail.status })}
        w={260}
      />
    ) : detail ? (
      <TextInput
        label={l('workflow.stageControl')}
        value={d(`statuses.${form.status}`)}
        readOnly
        w={260}
      />
    ) : null;

  const actionsCard = detail ? (
    <Panel flush title={d('record.cards.actions')} aria-label={d('record.cards.actions')}>
      {actions.length === 0 ? (
        <p className={`${classes.muted} ${classes.cardTable}`}>{d('actions.empty')}</p>
      ) : (
        <section
          className={`${table.tableWrap} ${classes.cardTable}`}
          aria-label={d('actions.table')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={`${table.table} ${classes.table}`}>
            <thead>
              <tr>
                {(
                  [
                    'no',
                    'description',
                    'responsible',
                    'targetOn',
                    'status',
                    'completedOn',
                    'evidence',
                    ...(editable ? ['edit'] : []),
                  ] as const
                ).map((c) => (
                  <th key={c} scope="col">
                    {d(`actions.columns.${c}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {actions.map((a, index) => (
                <tr key={a.id}>
                  <td>{index + 1}</td>
                  <td>{a.description}</td>
                  <td>{a.responsible ?? '—'}</td>
                  <td className={classes.nowrap}>{a.targetOn ? formatDay(a.targetOn) : '—'}</td>
                  <td>
                    <DefectActionBadge status={a.status} />
                  </td>
                  <td className={classes.nowrap}>
                    {a.completedOn ? formatDay(a.completedOn) : '—'}
                  </td>
                  <td>{(a.status === 'cancelled' ? a.cancelReason : a.evidence) ?? '—'}</td>
                  {editable ? (
                    <td>
                      <span className={classes.rowActions}>
                        <ActionIcon
                          variant="subtle"
                          aria-label={d('actions.edit', { no: index + 1 })}
                          onClick={() => setDialog({ kind: 'action', index })}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        {a.status === 'open' ? (
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={d('actions.remove', { no: index + 1 })}
                            onClick={() =>
                              void run(async () => {
                                await saveActions(actions.filter((_, i) => i !== index));
                                return null;
                              })
                            }
                          >
                            <IconX size={16} />
                          </ActionIcon>
                        ) : null}
                      </span>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {editable ? (
        <div className={classes.cardFoot}>
          <Button
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => setDialog({ kind: 'action', index: null })}
          >
            {d('actions.add')}
          </Button>
        </div>
      ) : null}
    </Panel>
  ) : null;

  const followupsCard = detail ? (
    <Panel flush title={d('record.cards.followups')} aria-label={d('record.cards.followups')}>
      {history.length === 0 ? (
        <p className={`${classes.muted} ${classes.cardTable}`}>{d('followups.empty')}</p>
      ) : (
        <section
          className={`${table.tableWrap} ${classes.cardTable}`}
          aria-label={d('followups.table')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={`${table.table} ${classes.table}`}>
            <thead>
              <tr>
                <th scope="col">{d('followups.columns.at')}</th>
                <th scope="col">{d('followups.columns.remarks')}</th>
                <th scope="col">{d('followups.columns.by')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td className={classes.nowrap}>{formatDateTime(h.at, h.offset)}</td>
                  <td>{h.body}</td>
                  <td>{h.by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {(ship && editor) || (!ship && can?.comment) ? (
        <div className={classes.cardFoot}>
          <Button
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => setDialog({ kind: ship ? 'followup' : 'comment' })}
          >
            {ship ? d('followups.add') : d('record.addComment')}
          </Button>
        </div>
      ) : null}
    </Panel>
  ) : null;

  /* ---------- Hızlı işlemler (rolüne göre; ADR 0096 §9) ---------- */

  const quick: QuickAction[] = [];
  if (detail && editor && !detail.closure.closed) {
    const open = detail.stage !== 'closed' && detail.closure.status !== 'pending';
    if (open && can?.workOrder !== false && editable) {
      quick.push({
        key: 'workOrder',
        label: l('quick.newWorkOrder'),
        icon: QUICK_ICONS.workOrder,
        disabled: detail.equipmentId === null,
        loading: busy,
        onClick: () =>
          void run(
            () =>
              editor.repairWorkOrder(detail.id, { additional: workOrders.length > 0 }) ??
              Promise.resolve(null),
          ),
      });
    }
    if (can?.nonConformity !== false && onNewNonConformity) {
      quick.push({
        key: 'nc',
        label: l('quick.openNc'),
        icon: QUICK_ICONS.nc,
        onClick: () => onNewNonConformity({ id: detail.id, defectNo: detail.defectNo }),
      });
    }
    if (open && can?.stock !== false && editor.issueStock && editor.stockOptions) {
      quick.push({
        key: 'stock',
        label: l('quick.useStock'),
        icon: QUICK_ICONS.stock,
        onClick: () => {
          setStock(null);
          setDialog({ kind: 'stock' });
          void editor
            .stockOptions?.()
            .then(setStock)
            .catch(() => setStock([]));
        },
      });
    }
    if (can?.requisition !== false && onNewRequisition) {
      quick.push({
        key: 'requisition',
        label: l('quick.newRequisition'),
        icon: QUICK_ICONS.requisition,
        wide: true,
        onClick: () => onNewRequisition({ id: detail.id, defectNo: detail.defectNo }),
      });
    }
    if (readyForVerification && editable) {
      quick.push({
        key: 'verify',
        label: l('quick.moveToVerification'),
        icon: QUICK_ICONS.plus,
        wide: true,
        loading: busy,
        onClick: () =>
          void run(
            () => editor.update(detail.id, detail.rowVersion, { status: 'verification' }),
            d('record.saved'),
          ),
      });
    }
    if (
      detail.status === 'verification' &&
      detail.closure.status !== 'pending' &&
      can?.approve &&
      // Onarımın reddi kaptanın kararı (ADR 0127 §1); eski sunucuda `approve` yeter
      can?.rejectRepair !== false &&
      editor.rejectRepair
    ) {
      quick.push({
        key: 'reject',
        label: l('quick.rejectRepair'),
        icon: <IconX size={14} />,
        wide: true,
        onClick: () => setDialog({ kind: 'reject' }),
      });
    }
  }

  /* ---------- Sekmeler ---------- */

  const counts = {
    workOrders: workOrders.length,
    nonConformities: links?.nonConformities.length ?? 0,
    material: (links?.materials.length ?? 0) + (links?.requisitions.length ?? 0),
    photos: attachments.length,
  };
  const documents: { key: string; text: string; open?: () => void }[] = [];
  if (detail?.sourceRef?.kind === 'hse_report') {
    const ref = detail.sourceRef;
    documents.push({
      key: 'hse',
      text: l('documents.hse', {
        type: t(`hse.types.${ref.reportType ?? ''}`, { defaultValue: ref.reportType ?? '' }),
        no: ref.no ?? '—',
      }),
      ...(onNonConformity && ref.reportType
        ? { open: () => onNonConformity({ id: ref.id, reportType: String(ref.reportType) }) }
        : {}),
    });
  }
  if (detail?.sourceRef?.kind === 'work_order') {
    const ref = detail.sourceRef;
    documents.push({
      key: 'wo',
      text: l('documents.workOrder', { no: ref.no ?? '—' }),
      ...(openWorkOrder ? { open: () => openWorkOrder(ref.id) } : {}),
    });
  }
  if (detail?.dailyReport) {
    const daily = detail.dailyReport;
    documents.push({
      key: 'daily',
      text: l('documents.daily', { no: daily.reportDay ? formatDay(daily.reportDay) : '—' }),
      ...(onDaily ? { open: () => onDaily(daily.id, daily.reportDay) } : {}),
    });
  }
  if (detail?.job) {
    documents.push({
      key: 'job',
      text: l('documents.job', { code: detail.job.code ?? '', title: detail.job.title ?? '' }),
    });
  }
  const visibleTabs: Tab[] = detail
    ? TABS.filter(
        (key) =>
          (key !== 'photos' || attachments.length > 0 || Boolean(editor?.upload)) &&
          (key !== 'documents' || documents.length > 0),
      )
    : ['form'];
  const tabLabel = (key: Tab): string => {
    switch (key) {
      case 'workOrders':
      case 'nonConformities':
      case 'material':
      case 'photos':
        return l(`tabs.${key}`, { n: counts[key] });
      default:
        return l(`tabs.${key}`);
    }
  };

  const formTab = (
    <div className={classes.tabPanel}>
      {hse}
      {!detail && options?.hseReport ? (
        <Alert color="blue">
          {l('newFrom.hse', {
            type: t(`hse.types.${options.hseReport.reportType}`, {
              defaultValue: options.hseReport.reportType,
            }),
            no: options.hseReport.reportNo,
          })}
        </Alert>
      ) : null}
      {!detail && options?.sourceWorkOrder ? (
        <Alert color="blue">{l('newFrom.workOrder', { no: options.sourceWorkOrder.woNo })}</Alert>
      ) : null}
      {partA}
      {partB}
      {partC}
      {sms}
      {more}
    </div>
  );

  const panels: Record<Tab, ReactNode> = {
    form: formTab,
    workflow: detail ? (
      <div className={classes.tabPanel}>
        {readyForVerification ? <Alert color="blue">{l('workflow.readyHint')}</Alert> : null}
        {stageControl}
        <WorkflowTable detail={detail} />
        <DefectClosureRoute
          detail={detail}
          scope={ship ? 'ship' : 'office'}
          decidable={canShipDecide || canOfficeDecide}
        />
        {actionsCard}
        {followupsCard}
      </div>
    ) : null,
    workOrders: detail ? (
      <div className={classes.tabPanel}>
        <span className={classes.muted}>{l('workOrders.hint')}</span>
        <WorkOrdersTable items={workOrders} onOpen={openWorkOrder} />
        {detail.equipmentId === null && editable ? (
          <span className={classes.muted}>{l('quick.needsEquipment')}</span>
        ) : null}
      </div>
    ) : null,
    nonConformities: detail ? (
      <div className={classes.tabPanel}>
        <NonConformityTable items={links?.nonConformities ?? []} onOpen={onNonConformity} />
      </div>
    ) : null,
    material: detail ? (
      <div className={classes.tabPanel}>
        <MaterialTables detail={detail} onRequisition={onRequisition} />
      </div>
    ) : null,
    photos: detail ? (
      <div className={classes.tabPanel}>
        <Panel title={l('photos')}>{photos}</Panel>
      </div>
    ) : null,
    documents: detail ? (
      <div className={classes.tabPanel}>
        <Panel title={l('documents.title')}>
          <ul className={classes.sideList}>
            {documents.map((doc) => (
              <li key={doc.key} className={classes.sideItem}>
                <span>{doc.text}</span>
                {doc.open ? (
                  <Button size="xs" variant="subtle" onClick={doc.open}>
                    {l('documents.open')}
                  </Button>
                ) : (
                  <span />
                )}
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    ) : null,
  };

  const actionDialog = dialog?.kind === 'action' ? dialog : null;
  const editing = actionDialog && actionDialog.index !== null ? actions[actionDialog.index] : null;

  return (
    <div className={classes.screen}>
      <Button
        className={classes.back}
        variant="subtle"
        size="xs"
        leftSection={<IconArrowLeft size={14} />}
        onClick={onBack}
      >
        {d('record.back')}
      </Button>
      {header}
      {detail ? (
        <div className={classes.lgsStepper}>
          <DefectStepper
            stage={detail.stage}
            tzOffsetMin={detail.tzOffsetMin}
            dates={{
              reported: detail.reportedAt,
              under_action: detail.captainActionOn,
              verification: detail.rectifiedOn,
              closed: detail.closedAt,
            }}
          />
        </div>
      ) : null}

      {/* Kapanışın iadesi (ADR 0116 Ek): iade eden, zaman, gerekçe */}
      {detail?.closure.status === 'returned' ? (
        <ReturnedBanner
          notice={detail.returnNotice}
          offsetMin={detail.tzOffsetMin}
          hint={Boolean(editor)}
        />
      ) : null}
      {!ship ? <Alert color="gray">{d('record.readOnly')}</Alert> : null}
      {ship && locked ? <Alert color="gray">{d('record.locked')}</Alert> : null}
      {options?.observation?.convertedTo && !detail ? (
        <Alert color="warning">
          {d('record.alreadyConverted', { no: options.observation.convertedTo.defectNo })}
        </Alert>
      ) : null}
      <DefectError message={error} />
      {notice ? (
        <Alert color="green" role="status">
          {notice}
        </Alert>
      ) : null}

      <div className={detail ? classes.lgsLayout : undefined}>
        <div className={classes.lgsMain}>
          <Tabs
            value={tab}
            onChange={(value) => setTab((value as Tab | null) ?? 'form')}
            keepMounted={false}
          >
            <Tabs.List aria-label={l('tabsLabel')}>
              {visibleTabs.map((key) => (
                <Tabs.Tab key={key} value={key}>
                  {tabLabel(key)}
                </Tabs.Tab>
              ))}
            </Tabs.List>
            {visibleTabs.map((key) => (
              <Tabs.Panel key={key} value={key}>
                {panels[key]}
              </Tabs.Panel>
            ))}
          </Tabs>
        </div>
        {detail ? (
          <SidePanel
            detail={detail}
            quick={quick}
            onWorkOrder={openWorkOrder}
            onNonConformity={onNonConformity}
            onRequisition={onRequisition}
          />
        ) : null}
      </div>

      <ActionDialog
        opened={actionDialog !== null}
        action={editing ?? null}
        onClose={() => setDialog(null)}
        onSave={async (action) => {
          const list = [...actions];
          if (actionDialog?.index === null || actionDialog?.index === undefined)
            list.push(action as never);
          else list[actionDialog.index] = { ...list[actionDialog.index], ...action } as never;
          await saveActions(list);
        }}
      />
      <NoteDialog
        opened={dialog?.kind === 'followup'}
        title={d('followups.dialogTitle')}
        label={d('followups.body')}
        hint={d('followups.hint')}
        onClose={() => setDialog(null)}
        onSave={async ({ body }) => {
          if (!editor || !detail) return;
          setDetail(await editor.followup(detail.id, body));
        }}
      />
      <NoteDialog
        opened={dialog?.kind === 'reject'}
        title={l('workflow.rejectTitle')}
        label={l('workflow.reason')}
        hint={l('workflow.rejectBody')}
        saveLabel={l('quick.rejectRepair')}
        onClose={() => setDialog(null)}
        onSave={async ({ body }) => {
          if (!editor?.rejectRepair || !detail) return;
          const next = await editor.rejectRepair(detail.id, detail.rowVersion, body);
          setDetail(next);
          setForm(formOf(next));
        }}
      />
      <NoteDialog
        opened={dialog?.kind === 'management' || dialog?.kind === 'comment'}
        title={dialog?.kind === 'management' ? d('office.noteTitle') : d('office.commentTitle')}
        label={d('office.body')}
        hint={d('office.hint')}
        withDay={dialog?.kind === 'management'}
        onClose={() => setDialog(null)}
        onSave={async ({ body, actionOn }) => {
          if (!source.office || !detail) return;
          const next = await source.office.note(detail.id, {
            kind: dialog?.kind === 'management' ? 'management_action' : 'comment',
            body,
            actionOn,
          });
          setDetail(next);
        }}
      />
      <StockDialog
        opened={dialog?.kind === 'stock'}
        options={stock}
        onClose={() => setDialog(null)}
        onSave={async (input) => {
          if (!editor?.issueStock || !detail) return;
          const next = await editor.issueStock(detail.id, input);
          setDetail(next);
          setForm(formOf(next));
        }}
      />
      <SignDialog
        opened={dialog?.kind === 'submit'}
        title={d('dialogs.submitTitle')}
        body={d('dialogs.submitBody')}
        reauthKind={reauthKind}
        onClose={() => setDialog(null)}
        onConfirm={async ({ password }) => {
          if (!editor || !detail) return;
          const next = await editor.submit(detail.id, password);
          setDetail(next);
          setForm(formOf(next));
        }}
      />
      <SignDialog
        opened={dialog?.kind === 'decide'}
        title={
          dialog?.kind === 'decide' && dialog.decision === 'returned'
            ? d('dialogs.decideReturn')
            : d('dialogs.decideApprove')
        }
        body={d('dialogs.decideBody')}
        reasonRequired={dialog?.kind === 'decide' && dialog.decision === 'returned'}
        reauthKind={reauthKind}
        onClose={() => setDialog(null)}
        onConfirm={async ({ password, reason }) =>
          decide({
            decision: dialog?.kind === 'decide' ? dialog.decision : 'approved',
            ...(password ? { password } : {}),
            reason,
          })
        }
      />
    </div>
  );
}

/** İmzalar = onay adımları (ADR 0060 §8): mürettebat (bildirim + gönderim), kaptan, yönetim. */
export function DefectSignatures({ detail }: { detail: DefectDetail | null }) {
  const { t } = useTranslation();
  const d = (key: string, options: Record<string, unknown> = {}) => t(`defects.${key}`, options);
  if (!detail) return null;
  const who = (s: DefectSignature, verb: string): ReactNode => (
    <>
      <span className={classes.signatureName}>
        {s.name ?? '—'}
        {s.roleName ? ` · ${s.roleName}` : ''}
      </span>
      <span className={classes.muted}>
        {d(`signatures.${verb}`, { at: formatDateTime(s.at, detail.tzOffsetMin) })}
      </span>
    </>
  );
  const submission = detail.signatures.submission;
  const steps = detail.signatures.steps;
  const step = (office: boolean): ReactNode => {
    if (!submission) return <span className={classes.muted}>{d('signatures.notSubmitted')}</span>;
    const found: DefectApprovalStep | undefined = steps.find((s) => s.office === office);
    if (!found) return <span className={classes.muted}>{d('signatures.notRequired')}</span>;
    if (!found.signature) {
      return (
        <span className={classes.muted}>
          {d('signatures.pending')}
          {found.roleName ? ` · ${found.roleName}` : ''}
        </span>
      );
    }
    return who(found.signature, found.decision === 'returned' ? 'returned' : 'approved');
  };
  return (
    <ul className={classes.signatures}>
      <li className={classes.signature}>
        <span className={classes.signatureLabel}>{d('signatures.crew')}</span>
        <span className={classes.signatureValue}>
          {who(detail.reportedBy, 'reported')}
          {submission ? (
            <span className={classes.muted}>
              {d('signatures.submitted', {
                at: `${formatDateTime(submission.at, detail.tzOffsetMin)} · ${submission.name ?? '—'}`,
              })}
            </span>
          ) : null}
        </span>
      </li>
      <li className={classes.signature}>
        <span className={classes.signatureLabel}>{d('signatures.captain')}</span>
        <span className={classes.signatureValue}>{step(false)}</span>
      </li>
      <li className={classes.signature}>
        <span className={classes.signatureLabel}>{d('signatures.management')}</span>
        <span className={classes.signatureValue}>{step(true)}</span>
      </li>
    </ul>
  );
}
